import React, { useImperativeHandle, useMemo, useRef } from 'react'
import { useTheme } from 'styled-components'

import { useCanvasPresentationFile } from '../../../../../components/WebRendererCanvas/hooks'

import LoadingState from '../../../components/LoadingState'
import { PageCanvasError } from '../PageCanvasError'
import { WebRendererCanvas } from '../../../../../components/WebRendererCanvas'

import {
  useInitialCameraPosition,
  useLastCameraPositionFromLocalStorage,
  SaveLastCameraPosition,
} from '../../hooks'

import CanvasContextMenu, { useCanvasContextMenu } from '../CanvasContextMenu'
import { CameraRefSync, OriginContent, useCameraRef } from '@sketch/canvas'
import { AnnotationsOverlay } from '../../../../canvas/components'
import { useCanvasAnnotations } from '../../../../annotations/hooks'

import {
  PageCanvasWrapper,
  StyledContainer,
  StyledPageCanvas,
} from './SharePageCanvas.styles'
import { getWebRenderCursor } from '@sketch/inspector'
import { useObjectProxyAccumulator } from '@sketch/utils'
import { DocumentHead, ErrorMessage } from '@sketch/components'
import { ReactComponent as PageCanvasIcon } from '@sketch/icons/page-canvas-64'
// Deep paths, not the `shares/FrameView` barrel: it re-exports FrameView,
// which mounts CanvasSurface, which renders this component - and the cycle
// breaks the bundler even though madge does not flag it.
import { FrameDetailInspectorOverlay } from '../../../FrameView/components/FrameDetailInspectorOverlay'
import { FrameDetailInspectorContextMenu } from '../../../FrameView/components/FrameDetailInspectorContextMenu'
import {
  useInspectorContext,
  useInspectorHoveredElement,
} from '../../../FrameView/hooks/inspector'
import { FrameDetailInspectorTestOverlay } from '../../../FrameView/components/FrameDetailInspectorTestOverlay'
import { PageCanvasFramesOverlays } from '../PageCanvasFramesOverlays'
import {
  useCopyLinkShortcut,
  useFocusCanvasEntity,
  useRestoreCanvasCamera,
} from '../../../../canvas/hooks'
import { useCanvasScope } from '../../../../canvas/scope'
import { useFlag } from '@sketch/modules-common'

const IS_RUNNING_TESTS =
  process.env.JEST_WORKER_ID !== undefined || window.Cypress

interface SharePageCanvasProps {
  permanentPageIdentifier: string
  /**
   * The focused entity - frame or Section - or undefined on the page canvas.
   *
   * It is no longer the renderer's scene root. Re-rooting resets the render
   * cache and snaps the camera to fit, which cut the move short; the entity is
   * picked out by fading the rest of the page instead, so the scene stays
   * rooted on the page and the camera can travel. This still scopes the
   * overlays and the remembered camera position to the entity.
   */
  focusedEntityUUID?: string
}

export const SharePageCanvas = function SharePageCanvas({
  ref,
  permanentPageIdentifier: pageUUID,
  focusedEntityUUID,
}: SharePageCanvasProps & {
  ref?: React.RefObject<HTMLDivElement | null>
}) {
  const theme = useTheme()
  const wrapperRef = useRef<HTMLDivElement>(
    null
  ) as React.RefObject<HTMLDivElement>

  const {
    share,
    page,
    versionShortId,
    hasError,
    isLoading,
    isRendering,
    showArtboardDecorations,
  } = useCanvasPresentationFile({
    pageUUID,
  })

  const scope = useCanvasScope()
  const isFocusAnimationEnabled = useFlag('canvas-focus-animation')

  // A frame holds no entities to drill into, so it is the one focus with
  // nothing to overlay. A Section shows its children; the page shows its own.
  const showFramesOverlays = scope?.focus.kind !== 'frame'

  const filePath = page?.presentationFile?.downloadUrl

  // Camera Position
  // Scoped by the focused entity so a focus view remembers its own position
  // rather than the page's, and the page canvas is not left pointing at
  // wherever the entity was.
  const initialCameraPosition = useInitialCameraPosition({
    pageUUID,
    versionShortId,
    rootEntityUUID: focusedEntityUUID,
  })

  const { initialZoom, initialPan } = initialCameraPosition

  // What is remembered on its own, rather than `initialCameraPosition`: a "Copy
  // Link to Position" link is for the canvas the renderer builds, not for every
  // later move between scene roots.
  const lastCameraPosition = useLastCameraPositionFromLocalStorage({
    pageUUID,
    versionShortId,
    rootEntityUUID: focusedEntityUUID,
  })

  // Enable Cmd+shift+C shortcut to copy page/frame URL
  useCopyLinkShortcut()

  // Focuses the entity, for a frame and a Section alike: behind
  // `canvas-focus-animation` the rest of the page fades back and the camera
  // travels to it, and without the flag it is hidden and cut to. With the
  // treatment on the move carries where the entity was left, so that it
  // travels back there rather than reframing it.
  useFocusCanvasEntity(isFocusAnimationEnabled ? lastCameraPosition : null)

  // Without the focus treatment the renderer ends up fitting whatever it is now
  // rooted on, so where that root was last left is put back over the fit. After
  // the isolation above, so it lands on the fit rather than under it.
  //
  // With the treatment on, the move above owns the camera and this would fight
  // it: `setPan` and `setZoom` do not cancel a camera animation - only a
  // gesture, a fit or disposal do - so the snap lands for a single frame and
  // the animation then carries on over it, from the position it captured
  // before. The move is given the remembered position instead, so it travels
  // there rather than being corrected once it arrives.
  useRestoreCanvasCamera(
    isFocusAnimationEnabled ? undefined : (lastCameraPosition ?? undefined)
  )

  // Read in handlers only, so kept in a ref rather than subscribed to: reading
  // the camera here would re-render the whole canvas on every frame of a drag.
  const cameraRef = useCameraRef()

  // Context Menu
  const {
    contextMenuPos,
    contextMenuMetaData,
    canvasContainerRef,
    handleContextMenu,
    handleCloseContextMenu,
  } = useCanvasContextMenu(cameraRef)

  // Annotations
  const annotationsContext = useCanvasAnnotations()

  const activeAnnotation = annotationsContext?.activeAnnotation
  const isViewingLatestVersion = annotationsContext?.isViewingLatestVersion
  const isPlacingDraftAnnotation =
    !!annotationsContext?.isPlacingDraftAnnotation
  const isAnnotationMoving = !!annotationsContext?.isAnnotationMoving
  const annotationPointerEvents = annotationsContext
    ? {
        onTouchStart: annotationsContext.onTouchStart,
        onTouchEnd: annotationsContext.onTouchEnd,
        onPointerDown: annotationsContext.onPointerDown,
        onPointerUp: annotationsContext.onPointerUp,
        onMouseDown: annotationsContext.onMouseDown,
        onMouseUp: annotationsContext.onMouseUp,
      }
    : undefined

  const { isInspectorActive, clickHandler, mouseMoveHandler, mouseDownHandle } =
    useInspectorContext()

  const hoveredElement = useInspectorHoveredElement()

  const webRenderContainerProps = useObjectProxyAccumulator(
    { onContextMenu: handleContextMenu },
    isInspectorActive
      ? {
          onClick: clickHandler,
          onMouseMove: mouseMoveHandler,
          onMouseDown: mouseDownHandle,
        }
      : annotationPointerEvents || {}
  )

  useImperativeHandle(ref, () => wrapperRef.current!)
  useImperativeHandle(canvasContainerRef, () => wrapperRef.current!)

  const memoizedContextMenuPosition = useMemo(
    () =>
      contextMenuPos
        ? {
            /**
             * On the Artboard view we have a custom context menu
             * that works similar to the already existing "useCanvasContextMenu"
             * so we just map the positions from one to the other
             */
            x: contextMenuPos.posX,
            y: contextMenuPos.posY,
          }
        : null,
    [contextMenuPos]
  )

  if (isLoading || isRendering) {
    return <LoadingState />
  }

  if (hasError) {
    return (
      <PageCanvasError
        description={
          <>
            There was a problem fetching the page data. You can find more
            information on our{' '}
            <a href="https://status.sketch.com/">Status Page</a>.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  if (!page) {
    return (
      <StyledContainer>
        <ErrorMessage
          icon={<PageCanvasIcon />}
          iconSize="medium"
          description={
            <>
              This page does not exist in the current version. <br />
              Select another page or another version.
            </>
          }
        />
      </StyledContainer>
    )
  }

  /**
   * By this point filePath shouldn't ever be undefined, but we narrow the type
   * to a defined string with this error case just to be sure.
   */
  if (!filePath) {
    return (
      <PageCanvasError
        description={
          <>
            Something went wrong while loading the canvas. Please reload — or
            try again later.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  /**
   * This error condition is a relic from the early MVP of the canvas that still
   * used the legacy JSON format. As such only internal Sketch users that may
   * still have a JSON PR file associated with their Sketch document will ever
   * see it. Safe to remove in future.
   */
  if (filePath.includes('.json')) {
    return (
      <PageCanvasError
        description={
          <>
            Please save a new document version in Sketch and reload to see this
            page as a canvas.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  return (
    <PageCanvasWrapper>
      {page?.name && share?.name && (
        <DocumentHead title={`${page.name} - ${share?.name}`} />
      )}

      <CameraRefSync cameraRef={cameraRef} />
      <SaveLastCameraPosition
        pageUUID={pageUUID}
        versionShortId={versionShortId}
        rootEntityUUID={focusedEntityUUID}
      />

      <StyledPageCanvas ref={wrapperRef}>
        <WebRendererCanvas
          pageUUID={pageUUID}
          page={page}
          versionShortId={versionShortId || undefined}
          shareIdentifier={share?.identifier}
          initialZoom={initialZoom}
          initialPan={initialPan}
          // Without the focus treatment, the entity is isolated the way it has
          // always been: by being the scene root. That resolves it at any
          // depth, which is what a Section nested in another Section needs -
          // the runtime isolation underneath only knows the page's own
          // children. With the treatment on, nothing is re-rooted.
          rootEntityUUID={
            isFocusAnimationEnabled ? undefined : focusedEntityUUID
          }
          backgroundColor={theme.colors.background.canvas.A}
          webRenderCursor={getWebRenderCursor(
            isPlacingDraftAnnotation,
            hoveredElement,
            isAnnotationMoving && !isViewingLatestVersion
          )}
          webRenderContainerProps={webRenderContainerProps.current}
          showArtboardDecorations={showArtboardDecorations}
        >
          <OriginContent>
            {showFramesOverlays && (
              <PageCanvasFramesOverlays
                focusedSectionUUID={focusedEntityUUID}
              />
            )}

            <AnnotationsOverlay
              activeAnnotationIdentifier={activeAnnotation}
              parentWrapper={canvasContainerRef}
              cameraRef={cameraRef}
            />
          </OriginContent>

          {isInspectorActive && (
            <>
              {IS_RUNNING_TESTS && <FrameDetailInspectorTestOverlay />}
              <FrameDetailInspectorOverlay
                canvasContainerRef={canvasContainerRef}
              />
            </>
          )}

          {isInspectorActive ? (
            <FrameDetailInspectorContextMenu
              contextMenuPosition={memoizedContextMenuPosition}
              headerEntityUUID={contextMenuMetaData?.headerEntityUUID ?? null}
              onCloseMenu={handleCloseContextMenu}
            />
          ) : (
            <CanvasContextMenu
              permanentPageId={pageUUID}
              closeTooltip={handleCloseContextMenu}
              contextMenuPos={contextMenuPos}
              contextMenuMetaData={contextMenuMetaData}
            />
          )}
        </WebRendererCanvas>
      </StyledPageCanvas>
    </PageCanvasWrapper>
  )
}
